import React, { useEffect, useState, useRef, useMemo } from 'react';
import Layout from '../components/Layout';
import { invoices, inventory as inventoryApi } from '../services/api';
import { Upload, Download, Trash2, FileText, X, Plus, Users, Loader2, Eye, CheckSquare, Square, ChevronDown, ChevronUp, ScanLine } from 'lucide-react';
import { useToast } from '../context/ToastContext';

interface LineItemForm { article_type: string; color: string; size: string; quantity: number; rate: number; }
interface BulkScanItem { file: File; status: 'pending' | 'scanning' | 'done' | 'error'; error?: string; dealer_id: number; dealer_name: string; date: string; amount: string; base_amount: string; gst_amount: string; invoice_number: string; duplicate?: { id: number; date: string | null; amount: number } | null; }

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const Invoices: React.FC = () => {
  const { toast, confirm } = useToast();
  const [items, setItems] = useState<any[]>([]);
  const [dealers, setDealers] = useState<any[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddDealer, setShowAddDealer] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [form, setForm] = useState({ dealer_id: 0, dealer_name: '', date: new Date().toISOString().split('T')[0], amount: '', notes: '' });
  const [dealerForm, setDealerForm] = useState({ name: '', contact: '', gst_number: '', notes: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<{ id: number; date: string | null; amount: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadLineItems, setUploadLineItems] = useState<LineItemForm[]>([]);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);

  // Filter
  const [filterMonth, setFilterMonth] = useState('all');

  // Expand rows
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Preview
  const [previewItem, setPreviewItem] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Bulk select
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Scan invoice
  const [showScan, setShowScan] = useState(false);
  const [bulkItems, setBulkItems] = useState<BulkScanItem[]>([]);
  const [bulkScanningIdx, setBulkScanningIdx] = useState(-1);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [isScanDragging, setIsScanDragging] = useState(false);

  const load = () => {
    invoices.getAll().then(r => setItems(r.data)).catch(() => {});
    invoices.getDealers().then(r => setDealers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (showUpload && inventoryItems.length === 0) {
      inventoryApi.getAll().then(r => setInventoryItems(r.data)).catch(() => {});
    }
  }, [showUpload]);

  // Month options derived from loaded data
  const monthOptions = useMemo(() => {
    const seen = new Set<string>();
    items.forEach(item => {
      const d = new Date(item.date);
      seen.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    });
    return Array.from(seen).sort().reverse();
  }, [items]);

  const filteredItems = useMemo(() => {
    if (filterMonth === 'all') return items;
    const [fy, fm] = filterMonth.split('-').map(Number);
    return items.filter(item => {
      const d = new Date(item.date);
      return d.getFullYear() === fy && d.getMonth() + 1 === fm;
    });
  }, [items, filterMonth]);

  const formatMonthLabel = (ym: string) => {
    const [y, m] = ym.split('-').map(Number);
    return `${MONTHS[m - 1]} ${y}`;
  };

  const openScan = () => {
    setBulkItems([]); setBulkScanningIdx(-1); setShowScan(true);
  };

  const handleBulkFiles = async (files: FileList | File[]) => {
    const pdfs = Array.from(files).filter(f => f.type === 'application/pdf').slice(0, 10);
    if (pdfs.length === 0) return;
    const initial: BulkScanItem[] = pdfs.map(f => ({ file: f, status: 'pending', dealer_id: 0, dealer_name: '', date: new Date().toISOString().split('T')[0], amount: '', base_amount: '', gst_amount: '', invoice_number: '', duplicate: null }));
    setBulkItems(initial);
    const results = [...initial];
    for (let i = 0; i < results.length; i++) {
      setBulkScanningIdx(i);
      results[i] = { ...results[i], status: 'scanning' };
      setBulkItems([...results]);
      try {
        const res = await invoices.extractAmount(results[i].file);
        const d = res.data;
        results[i] = { ...results[i], status: 'done', dealer_name: d.supplier || '', date: d.date || new Date().toISOString().split('T')[0], amount: d.amount != null ? String(d.amount) : '', base_amount: d.base_amount != null ? String(d.base_amount) : '', gst_amount: d.gst_amount != null ? String(d.gst_amount) : '', invoice_number: d.invoice_number || '', duplicate: d.duplicate || null };
      } catch {
        results[i] = { ...results[i], status: 'error', error: 'Scan failed' };
      }
      setBulkItems([...results]);
    }
    setBulkScanningIdx(-1);
  };

  const updateBulkItem = (idx: number, patch: Partial<BulkScanItem>) => {
    setBulkItems(prev => { const u = [...prev]; u[idx] = { ...u[idx], ...patch }; return u; });
  };

  const handleBulkSave = async () => {
    const saveable = bulkItems.filter(it => it.status === 'done' && it.dealer_name && it.amount);
    if (saveable.length === 0) return;
    setBulkSaving(true);
    let saved = 0; let failed = 0;
    for (const item of saveable) {
      try {
        await invoices.upload(item.file, item.dealer_name, item.date, Number(item.amount), '', item.dealer_id || undefined, item.base_amount ? Number(item.base_amount) : undefined, item.gst_amount ? Number(item.gst_amount) : undefined, item.invoice_number || undefined);
        saved++;
      } catch { failed++; }
    }
    setBulkSaving(false); setShowScan(false); load();
    if (failed === 0) toast(`${saved} invoice${saved > 1 ? 's' : ''} saved`, 'success');
    else toast(`${saved} saved, ${failed} failed`, 'error');
  };

  const processFile = async (file: File) => {
    setSelectedFile(file);
    setDuplicateWarning(null);
    if (file.type === 'application/pdf') {
      setExtracting(true);
      try {
        const res = await invoices.extractAmount(file);
        const d = res.data;
        setDuplicateWarning(d.duplicate || null);
        setForm(f => ({
          ...f,
          ...(d.amount ? { amount: d.amount.toString() } : {}),
          ...(d.date ? { date: d.date } : {}),
          ...(d.supplier && !f.dealer_name ? { dealer_name: d.supplier } : {}),
        }));
      } catch {}
      setExtracting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleDealerSelect = (dealerId: number) => {
    const dealer = dealers.find(d => d.id === dealerId);
    setForm({ ...form, dealer_id: dealerId, dealer_name: dealer?.name || '' });
  };

  // Line item helpers
  const addLineItem = () => setUploadLineItems(prev => [...prev, { article_type: '', color: '', size: '', quantity: 1, rate: 0 }]);
  const removeLineItem = (i: number) => setUploadLineItems(prev => prev.filter((_, idx) => idx !== i));
  const updateLineItem = (i: number, field: keyof LineItemForm, value: any) => {
    setUploadLineItems(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });
  };
  const updateLineItemMulti = (i: number, fields: Partial<LineItemForm>) => {
    setUploadLineItems(prev => { const u = [...prev]; u[i] = { ...u[i], ...fields }; return u; });
  };

  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL'];
  const articleTypes = useMemo(() => Array.from(new Set(inventoryItems.map(i => i.article_type))).sort() as string[], [inventoryItems]);
  const colorsFor = (article: string) => Array.from(new Set(inventoryItems.filter(i => i.article_type === article).map(i => i.color))).sort() as string[];
  const sizesFor = (article: string, color: string) => {
    const raw = Array.from(new Set(inventoryItems.filter(i => i.article_type === article && i.color === color).map(i => i.size))) as string[];
    return raw.sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a); const bi = SIZE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !form.dealer_name || !form.amount) return;
    setUploading(true);
    try {
      const res = await invoices.upload(selectedFile, form.dealer_name, form.date, Number(form.amount), form.notes, form.dealer_id || undefined);
      const invoiceId = res.data.id;
      const validItems = uploadLineItems.filter(li => li.article_type.trim() && li.quantity > 0 && li.rate >= 0);
      if (validItems.length > 0) {
        await invoices.saveItems(invoiceId, validItems);
      }
      setShowUpload(false);
      setForm({ dealer_id: 0, dealer_name: '', date: new Date().toISOString().split('T')[0], amount: '', notes: '' });
      setSelectedFile(null);
      setDuplicateWarning(null);
      setUploadLineItems([]);
      load();
      toast('Invoice uploaded successfully', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Upload failed', 'error');
    }
    setUploading(false);
  };

  const handleAddDealer = async () => {
    if (!dealerForm.name) return;
    try {
      await invoices.createDealer(dealerForm);
      setDealerForm({ name: '', contact: '', gst_number: '', notes: '' });
      setShowAddDealer(false);
      load();
      toast('Dealer added', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Failed to add dealer', 'error');
    }
  };

  const handleDownload = async (item: any) => {
    try {
      const res = await invoices.download(item.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = item.filename; a.click();
    } catch {}
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm('Delete this invoice?');
    if (!ok) return;
    await invoices.delete(id);
    load();
    toast('Invoice deleted', 'success');
  };

  const handleDeleteDealer = async (id: number) => {
    const ok = await confirm('Delete this dealer?');
    if (!ok) return;
    await invoices.deleteDealer(id);
    load();
  };

  const handlePreview = async (item: any) => {
    setPreviewItem(item);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await invoices.download(item.id);
      const blob = new Blob([res.data]);
      const ext = (item.filename || '').toLowerCase();
      let type = 'application/octet-stream';
      if (ext.endsWith('.pdf')) type = 'application/pdf';
      else if (ext.endsWith('.png')) type = 'image/png';
      else if (ext.endsWith('.jpg') || ext.endsWith('.jpeg')) type = 'image/jpeg';
      else if (ext.endsWith('.webp')) type = 'image/webp';
      setPreviewUrl(URL.createObjectURL(new Blob([blob], { type })));
    } catch { setPreviewUrl(null); }
    setPreviewLoading(false);
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewItem(null); setPreviewUrl(null);
  };

  const isPreviewable = (filename: string) => /\.(pdf|png|jpe?g|webp)$/i.test(filename || '');
  const isImage = (filename: string) => /\.(png|jpe?g|webp)$/i.test(filename || '');

  const toggleBulkSelect = (id: number) => {
    setBulkSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const selectAll = () => {
    if (bulkSelected.size === filteredItems.length) setBulkSelected(new Set());
    else setBulkSelected(new Set(filteredItems.map(i => i.id)));
  };

  const handleBulkDownload = async () => {
    if (bulkSelected.size === 0) return;
    setBulkDownloading(true);
    try {
      const ids = filteredItems.filter(i => bulkSelected.has(i.id)).map(i => i.id);
      const res = await invoices.bulkDownload(ids);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
    setBulkDownloading(false);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const totalAmount = filteredItems.reduce((s, i) => s + (i.amount || 0), 0);
  const totalGst = filteredItems.reduce((s, i) => s + (i.gst_amount || 0), 0);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px', background: 'var(--bg-input)',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: 0.8, display: 'block', marginBottom: 6, fontWeight: 600,
  };
  const smallInput: React.CSSProperties = {
    padding: '7px 9px', background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text-primary)', fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box',
  };

  return (
    <Layout title="Purchase Invoices" subtitle="Store and manage dealer invoices">
      {/* Header KPIs + actions */}
      <div className="animate-in" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px', minWidth: 130 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Invoices</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.5 }}>{filteredItems.length}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px', minWidth: 160 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Total Amount</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-amber)', letterSpacing: -0.5 }}>₹{totalAmount.toLocaleString('en-IN')}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px', minWidth: 160 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Total GST Paid</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-red)', letterSpacing: -0.5 }}>{totalGst > 0 ? `₹${totalGst.toLocaleString('en-IN')}` : '—'}</div>
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 22px', minWidth: 110 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, fontWeight: 600 }}>Dealers</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent-purple)', letterSpacing: -0.5 }}>{dealers.length}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowAddDealer(true)} style={{ padding: '9px 16px', background: 'var(--accent-purple-soft)', border: '1px solid rgba(157,122,255,0.15)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-purple)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} /> Manage Dealers</button>
          <button onClick={openScan} style={{ padding: '9px 16px', background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-green)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><ScanLine size={14} /> Scan Invoice</button>
          <button onClick={() => setShowUpload(true)} style={{ padding: '9px 18px', background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}><Plus size={15} /> Upload Invoice</button>
        </div>
      </div>

      {/* Month filter tabs */}
      <div className="animate-in" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilterMonth('all')}
          style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)', background: filterMonth === 'all' ? 'var(--accent-blue)' : 'var(--bg-card)', color: filterMonth === 'all' ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >All</button>
        {monthOptions.map(ym => (
          <button key={ym}
            onClick={() => setFilterMonth(ym)}
            style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)', background: filterMonth === ym ? 'var(--accent-blue)' : 'var(--bg-card)', color: filterMonth === ym ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >{formatMonthLabel(ym)}</button>
        ))}
      </div>

      {/* Bulk action bar */}
      {bulkSelected.size > 0 && (
        <div className="animate-in" style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.2)', borderRadius: 'var(--radius-md)', padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)' }}>{bulkSelected.size} selected</span>
          <button onClick={handleBulkDownload} disabled={bulkDownloading} style={{ padding: '6px 14px', background: 'var(--accent-green)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            {bulkDownloading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
            {bulkDownloading ? 'Downloading...' : 'Download All'}
          </button>
          <button onClick={() => setBulkSelected(new Set())} style={{ padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Clear</button>
        </div>
      )}

      {/* Invoices Table */}
      <div className="animate-in-1" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div className="table-responsive">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px 14px', width: 40 }}>
                  <button onClick={selectAll} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    {bulkSelected.size === filteredItems.length && filteredItems.length > 0
                      ? <CheckSquare size={15} color="var(--accent-blue)" />
                      : <Square size={15} color="var(--text-muted)" />}
                  </button>
                </th>
                {['Dealer', 'Date', 'Inv No.', 'Base', 'GST', 'Total', 'Items', 'File', 'Notes', ''].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item: any) => (
                <React.Fragment key={item.id}>
                  <tr style={{ borderBottom: expandedId === item.id ? 'none' : '1px solid var(--border)', background: bulkSelected.has(item.id) ? 'var(--accent-blue-soft)' : undefined }}>
                    <td style={{ padding: '12px 14px' }}>
                      <button onClick={() => toggleBulkSelect(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        {bulkSelected.has(item.id) ? <CheckSquare size={15} color="var(--accent-blue)" /> : <Square size={15} color="var(--text-muted)" />}
                      </button>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{item.dealer_name}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{item.invoice_number || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-secondary)' }}>{item.base_amount != null ? `₹${item.base_amount.toLocaleString('en-IN')}` : '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--accent-red)' }}>{item.gst_amount != null ? `₹${item.gst_amount.toLocaleString('en-IN')}` : '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--accent-amber)', fontWeight: 600 }}>₹{(item.amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {item.items && item.items.length > 0 ? (
                        <button
                          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                          style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.2)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: 'var(--accent-blue)' }}
                        >
                          {expandedId === item.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          {item.items.length} item{item.items.length !== 1 ? 's' : ''}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={14} color="var(--accent-blue)" />
                        <span style={{ fontSize: 12.5, color: 'var(--accent-blue)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.filename}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatSize(item.file_size || 0)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>{item.notes || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {isPreviewable(item.filename) && (
                          <button onClick={() => handlePreview(item)} title="Preview" style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.15)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}><Eye size={13} color="var(--accent-blue)" /></button>
                        )}
                        <button onClick={() => handleDownload(item)} title="Download" style={{ background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.15)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}><Download size={12} color="var(--accent-green)" /></button>
                        <button onClick={() => handleDelete(item.id)} title="Delete" style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex' }}><Trash2 size={13} color="var(--accent-red)" /></button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded line items row */}
                  {expandedId === item.id && item.items && item.items.length > 0 && (
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td colSpan={11} style={{ padding: '0 16px 16px 52px', background: 'var(--bg-elevated)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                          <thead>
                            <tr>
                              {['Article', 'Color', 'Size', 'Qty', 'Rate (₹)', 'Amount (₹)'].map(h => (
                                <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {item.items.map((li: any, i: number) => (
                              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                                <td style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{li.article_type}</td>
                                <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>{li.color}</td>
                                <td style={{ padding: '6px 10px' }}><span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>{li.size}</span></td>
                                <td style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{li.quantity}</td>
                                <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>₹{li.rate?.toLocaleString('en-IN')}</td>
                                <td style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 700, color: 'var(--accent-amber)' }}>₹{li.amount?.toLocaleString('en-IN')}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={5} style={{ padding: '6px 10px', fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'right', fontWeight: 600 }}>Total Items Amount</td>
                              <td style={{ padding: '6px 10px', fontSize: 13, fontWeight: 800, color: 'var(--accent-amber)' }}>
                                ₹{item.items.reduce((s: number, li: any) => s + (li.amount || 0), 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)', fontSize: 13 }}>
            {items.length === 0 ? 'No invoices uploaded yet. Add a dealer first, then upload invoices.' : 'No invoices for the selected month.'}
          </div>
        )}
      </div>

      {/* ═══ PREVIEW MODAL ═══ */}
      {previewItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) closePreview(); }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', width: '90vw', maxWidth: 900, height: '88vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-elevated)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{previewItem.filename}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{previewItem.dealer_name} · {new Date(previewItem.date).toLocaleDateString('en-IN')} · <span style={{ color: 'var(--accent-amber)', fontWeight: 600 }}>₹{(previewItem.amount || 0).toLocaleString('en-IN')}</span></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => handleDownload(previewItem)} style={{ padding: '7px 14px', background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.15)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-green)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><Download size={13} /> Download</button>
                <button onClick={closePreview} style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} color="var(--text-muted)" /></button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
              {previewLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading preview...</div>
              ) : previewUrl ? (
                isImage(previewItem.filename)
                  ? <img src={previewUrl} alt={previewItem.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <iframe src={previewUrl} title={previewItem.filename} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Preview not available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ UPLOAD INVOICE MODAL ═══ */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) { setShowUpload(false); setUploadLineItems([]); } }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 28, width: 620, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>Upload Invoice</div>
              <button onClick={() => { setShowUpload(false); setUploadLineItems([]); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            {/* Dealer */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Dealer</label>
              {dealers.length > 0 ? (
                <select value={form.dealer_id} onChange={e => handleDealerSelect(Number(e.target.value))} style={inputStyle}>
                  <option value={0}>Select a dealer...</option>
                  {dealers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              ) : (
                <div style={{ padding: '11px 14px', background: 'var(--accent-amber-soft)', border: '1px solid rgba(245,166,35,0.15)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--accent-amber)' }}>
                  No dealers added yet. Click "Manage Dealers" to add one first.
                </div>
              )}
            </div>

            {/* File */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Invoice File</label>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${selectedFile ? 'var(--accent-green)' : isDragging ? 'var(--accent-blue)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-md)', padding: '16px', textAlign: 'center', cursor: 'pointer',
                  background: selectedFile ? 'var(--accent-green-soft)' : isDragging ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input type="file" ref={fileRef} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />
                {selectedFile ? (
                  <div style={{ position: 'relative' }}>
                    <FileText size={18} color="var(--accent-green)" style={{ marginBottom: 4 }} />
                    <div style={{ fontSize: 13, color: 'var(--accent-green)', fontWeight: 600 }}>{selectedFile.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{formatSize(selectedFile.size)}</div>
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                      title="Remove file"
                      style={{ position: 'absolute', top: -4, right: -4, background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                    ><X size={11} color="var(--accent-red)" /></button>
                  </div>
                ) : isDragging ? (
                  <div><Upload size={18} color="var(--accent-blue)" style={{ marginBottom: 4 }} /><div style={{ fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>Drop file here</div></div>
                ) : (
                  <div><Upload size={18} color="var(--text-muted)" style={{ marginBottom: 4, opacity: 0.5 }} /><div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Click to select or drag & drop</div><div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, opacity: 0.6 }}>PDF (auto-extracts amount), Images, Excel</div></div>
                )}
              </div>
            </div>

            {duplicateWarning && (
              <div style={{ marginBottom: 14, padding: '11px 14px', background: 'var(--accent-amber-soft)', border: '1px solid rgba(245,166,35,0.15)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: 'var(--accent-amber)' }}>
                ⚠️ Possible duplicate — an invoice from this dealer with this invoice number was already saved on {duplicateWarning.date ? new Date(duplicateWarning.date).toLocaleDateString('en-IN') : 'an earlier date'} for ₹{duplicateWarning.amount.toLocaleString('en-IN')}.
              </div>
            )}

            {/* Date + Amount */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Date {extracting && <span style={{ color: 'var(--accent-blue)', marginLeft: 6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Extracting...</span>}</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Amount (₹) {extracting && <span style={{ color: 'var(--accent-blue)', marginLeft: 6, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Extracting...</span>}</label>
                <div style={{ position: 'relative' }}>
                  <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} placeholder="0" />
                  {extracting && <Loader2 size={13} color="var(--accent-blue)" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', animation: 'spin 1s linear infinite' }} />}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Notes (optional)</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle} placeholder="e.g. Batch #4, urgent restock" />
            </div>

            {/* ═══ PRODUCT LINE ITEMS ═══ */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Product Line Items <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></div>
                <button onClick={addLineItem} style={{ background: 'var(--accent-blue-soft)', border: '1px solid rgba(79,125,249,0.2)', borderRadius: 6, padding: '5px 11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--accent-blue)' }}><Plus size={12} /> Add Item</button>
              </div>

              {uploadLineItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: 12, background: 'var(--bg-elevated)', borderRadius: 8 }}>
                  Click "Add Item" to record what products were in this invoice
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 1fr 32px', gap: 6 }}>
                    {['Article', 'Color', 'Size', 'Qty', 'Rate ₹', ''].map(h => (
                      <div key={h} style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, paddingBottom: 2 }}>{h}</div>
                    ))}
                  </div>
                  {uploadLineItems.map((li, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 1fr 32px', gap: 6, alignItems: 'center' }}>
                      <select value={li.article_type} onChange={e => updateLineItemMulti(i, { article_type: e.target.value, color: '', size: '' })} style={smallInput}>
                        <option value="">Select article...</option>
                        {articleTypes.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <select value={li.color} onChange={e => updateLineItemMulti(i, { color: e.target.value, size: '' })} style={smallInput} disabled={!li.article_type}>
                        <option value="">Select color...</option>
                        {colorsFor(li.article_type).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select value={li.size} onChange={e => updateLineItem(i, 'size', e.target.value)} style={smallInput} disabled={!li.color}>
                        <option value="">Select size...</option>
                        {sizesFor(li.article_type, li.color).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input type="number" min={1} value={li.quantity} onChange={e => updateLineItem(i, 'quantity', Number(e.target.value))} style={smallInput} />
                      <input type="number" min={0} step={0.01} value={li.rate} onChange={e => updateLineItem(i, 'rate', Number(e.target.value))} style={smallInput} />
                      <button onClick={() => removeLineItem(i)} style={{ background: 'var(--accent-red-soft)', border: 'none', borderRadius: 5, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}><X size={12} color="var(--accent-red)" /></button>
                    </div>
                  ))}
                  {uploadLineItems.length > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'right', paddingTop: 4 }}>
                      Total: ₹{uploadLineItems.reduce((s, li) => s + li.quantity * li.rate, 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} across {uploadLineItems.reduce((s, li) => s + li.quantity, 0)} units
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleUpload} disabled={uploading || !selectedFile || !form.dealer_name || !form.amount} style={{ flex: 1, padding: 12, background: (selectedFile && form.dealer_name && form.amount) ? 'linear-gradient(135deg, var(--accent-blue), var(--accent-purple))' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: (selectedFile && form.dealer_name && form.amount) ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: (selectedFile && form.dealer_name && form.amount) ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Invoice'}
              </button>
              <button onClick={() => { setShowUpload(false); setUploadLineItems([]); setDuplicateWarning(null); }} style={{ padding: '12px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Dealers Modal */}
      {showAddDealer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget) setShowAddDealer(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 0, width: 480, boxShadow: 'var(--shadow-elevated)', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'var(--bg-card)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', zIndex: 2 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Manage Dealers</div>
              <button onClick={() => setShowAddDealer(false)} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>Add New Dealer</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div><label style={labelStyle}>Name *</label><input value={dealerForm.name} onChange={e => setDealerForm({ ...dealerForm, name: e.target.value })} style={inputStyle} placeholder="e.g. Fabric World" /></div>
                <div><label style={labelStyle}>Contact</label><input value={dealerForm.contact} onChange={e => setDealerForm({ ...dealerForm, contact: e.target.value })} style={inputStyle} placeholder="Phone or email" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                <div><label style={labelStyle}>GST Number</label><input value={dealerForm.gst_number} onChange={e => setDealerForm({ ...dealerForm, gst_number: e.target.value })} style={inputStyle} placeholder="e.g. 24ABCDE1234F1Z5" /></div>
                <div><label style={labelStyle}>Notes</label><input value={dealerForm.notes} onChange={e => setDealerForm({ ...dealerForm, notes: e.target.value })} style={inputStyle} placeholder="Optional" /></div>
              </div>
              <button onClick={handleAddDealer} disabled={!dealerForm.name} style={{ padding: '10px 18px', width: '100%', marginBottom: 24, background: dealerForm.name ? 'linear-gradient(135deg, var(--accent-purple), #7c3aed)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: dealerForm.name ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: dealerForm.name ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}><Plus size={14} /> Add Dealer</button>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>Your Dealers ({dealers.length})</div>
              {dealers.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dealers.map((d: any) => (
                    <div key={d.id} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{[d.contact, d.gst_number].filter(Boolean).join(' · ') || 'No details'}</div>
                      </div>
                      <button onClick={() => handleDeleteDealer(d.id)} style={{ background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.12)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', display: 'flex' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No dealers yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ SCAN INVOICE MODAL ═══ */}
      {showScan && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={e => { if (e.target === e.currentTarget && bulkScanningIdx === -1 && !bulkSaving) setShowScan(false); }}>
          <div className="animate-in" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 28, width: bulkItems.length > 0 ? 660 : 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-elevated)' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {bulkItems.length === 0 ? 'Scan Invoices' : bulkScanningIdx >= 0 ? `Scanning ${bulkScanningIdx + 1} of ${bulkItems.length}...` : 'Review & Save'}
                </div>
                {bulkItems.length > 0 && bulkScanningIdx === -1 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>AI-extracted — check and edit before saving</div>}
              </div>
              <button onClick={() => { if (bulkScanningIdx === -1 && !bulkSaving) setShowScan(false); }} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} color="var(--text-muted)" /></button>
            </div>

            {/* Drop zone */}
            {bulkItems.length === 0 && (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Upload up to 10 supplier PDFs — Claude Vision extracts details for each</div>
                <div
                  onClick={() => (document.getElementById('bulkScanInput') as HTMLInputElement)?.click()}
                  onDragOver={e => { e.preventDefault(); setIsScanDragging(true); }}
                  onDragLeave={e => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsScanDragging(false); }}
                  onDrop={e => { e.preventDefault(); setIsScanDragging(false); if (e.dataTransfer.files.length) handleBulkFiles(e.dataTransfer.files); }}
                  style={{ border: `2px dashed ${isScanDragging ? 'var(--accent-blue)' : 'var(--border)'}`, borderRadius: 10, padding: '44px 24px', textAlign: 'center', cursor: 'pointer', background: isScanDragging ? 'var(--accent-blue-soft)' : 'var(--bg-elevated)', transition: 'border-color 0.15s, background 0.15s' }}
                >
                  <input id="bulkScanInput" type="file" accept=".pdf" multiple style={{ display: 'none' }} onChange={e => { if (e.target.files?.length) handleBulkFiles(e.target.files); }} />
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📄</div>
                  {isScanDragging
                    ? <div style={{ fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>Drop PDFs here</div>
                    : <><div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600, marginBottom: 4 }}>Click to select or drag & drop PDFs</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Up to 10 PDFs — each scanned by AI</div></>
                  }
                </div>
              </div>
            )}

            {/* Scanning progress banner */}
            {bulkItems.length > 0 && bulkScanningIdx >= 0 && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, fontSize: 13, color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                Scanning {bulkItems[bulkScanningIdx]?.file.name}...
              </div>
            )}

            {/* Review cards */}
            {bulkItems.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {bulkItems.map((item, idx) => (
                  <div key={idx} style={{ border: `1px solid ${item.status === 'error' ? 'rgba(239,90,111,0.3)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', background: item.status === 'scanning' ? 'rgba(59,130,246,0.04)' : item.status === 'error' ? 'rgba(239,90,111,0.04)' : 'var(--bg-elevated)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: item.status === 'done' ? 10 : 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.status === 'scanning' && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                        {item.status === 'error' && <span style={{ color: 'var(--accent-red)' }}>✕</span>}
                        {item.status === 'done' && <span style={{ color: 'var(--accent-green)' }}>✓</span>}
                        {item.status === 'pending' && <span style={{ opacity: 0.4 }}>○</span>}
                        <span style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</span>
                      </div>
                      <button onClick={() => setBulkItems(p => p.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)' }}><X size={13} /></button>
                    </div>
                    {item.status === 'error' && <div style={{ fontSize: 11, color: 'var(--accent-red)', marginTop: 4 }}>{item.error}</div>}
                    {item.status === 'done' && (
                      <>
                        {item.duplicate && (
                          <div style={{ marginBottom: 8, padding: '8px 10px', background: 'var(--accent-amber-soft)', border: '1px solid rgba(245,166,35,0.15)', borderRadius: 6, fontSize: 11, color: 'var(--accent-amber)' }}>
                            ⚠️ Possible duplicate — saved on {item.duplicate.date ? new Date(item.duplicate.date).toLocaleDateString('en-IN') : 'an earlier date'} for ₹{item.duplicate.amount.toLocaleString('en-IN')}.
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 8 }}>
                          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dealer</div>
                              <select value={item.dealer_id} onChange={e => { const d = dealers.find((x: any) => x.id === Number(e.target.value)); updateBulkItem(idx, { dealer_id: Number(e.target.value), dealer_name: d?.name || item.dealer_name }); }} style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', marginBottom: item.dealer_id === 0 ? 4 : 0 }}>
                                <option value={0}>{item.dealer_name || 'Select...'}</option>
                                {dealers.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
                              </select>
                              {item.dealer_id === 0 && <input value={item.dealer_name} onChange={e => updateBulkItem(idx, { dealer_name: e.target.value })} placeholder="Dealer name" style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />}
                            </div>
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invoice #</div>
                              <input value={item.invoice_number} onChange={e => updateBulkItem(idx, { invoice_number: e.target.value })} placeholder="e.g. INV-001" style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</div>
                            <input type="date" value={item.date} onChange={e => updateBulkItem(idx, { date: e.target.value })} style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Base (₹)</div>
                            <input type="number" value={item.base_amount} onChange={e => updateBulkItem(idx, { base_amount: e.target.value })} placeholder="0" style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>GST (₹)</div>
                            <input type="number" value={item.gst_amount} onChange={e => updateBulkItem(idx, { gst_amount: e.target.value })} placeholder="0" style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total (₹) *</div>
                            <input type="number" value={item.amount} onChange={e => updateBulkItem(idx, { amount: e.target.value })} placeholder="0" style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: `1px solid ${item.amount ? 'var(--border)' : 'rgba(239,90,111,0.4)'}`, background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {bulkScanningIdx === -1 && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                    <button onClick={() => setShowScan(false)} style={{ flex: 1, padding: 11, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleBulkSave} disabled={bulkSaving || bulkItems.filter(it => it.status === 'done' && it.dealer_name && it.amount).length === 0}
                      style={{ flex: 2, padding: 11, background: (!bulkSaving && bulkItems.some(it => it.status === 'done' && it.dealer_name && it.amount)) ? 'linear-gradient(135deg, var(--accent-green), #059669)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: (!bulkSaving && bulkItems.some(it => it.status === 'done' && it.dealer_name && it.amount)) ? '#fff' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                      {bulkSaving ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</> : <><ScanLine size={14} /> Save {bulkItems.filter(it => it.status === 'done' && it.dealer_name && it.amount).length} Invoice{bulkItems.filter(it => it.status === 'done' && it.dealer_name && it.amount).length !== 1 ? 's' : ''}</>}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default Invoices;
